import { inject, InjectionToken, ValueProvider } from '@angular/core';

export interface HlmDatePickerConfig<T> {
  /**
   * Defines how the date should be displayed in the UI.
   *
   * @param date
   * @returns formatted date
   */
  formatDate: (date: T) => string;

  /**
   * Defines how the date should be transformed before saving to model/form.
   *
   * @param date
   * @returns transformed date
   */
  transformDate: (date: T) => T;
}

function getDefaultConfig<T>(): HlmDatePickerConfig<T> {
  return {
    formatDate: (date) => (date instanceof Date ? date.toDateString() : `${date}`),
    transformDate: (date) => date,
  };
}

const HlmDatePickerConfigToken = new InjectionToken<HlmDatePickerConfig<unknown>>('HlmDatePickerConfig');

export function provideHlmDatePickerConfig<T>(config: Partial<HlmDatePickerConfig<T>>): ValueProvider {
  return { provide: HlmDatePickerConfigToken, useValue: { ...getDefaultConfig(), ...config } };
}

export function injectHlmDatePickerConfig<T>(): HlmDatePickerConfig<T> {
  const injectedConfig = inject(HlmDatePickerConfigToken, { optional: true });
  return injectedConfig ? (injectedConfig as HlmDatePickerConfig<T>) : getDefaultConfig();
}
